import { useEffect, useState } from "react";
import { ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * One-time (per version) Beta Acknowledgment modal.
 *
 * Displays for any signed-in user whose ``beta_ack_version_accepted`` is
 * missing or lower than the server-required ``beta_ack_required_version``.
 * The modal is intentionally non-dismissible — no close button, no
 * backdrop-click, no ESC handler — until the checkbox is ticked and the
 * server records their acknowledgment. Bumping ``BETA_ACK_VERSION`` on the
 * backend re-prompts every user on their next sign-in.
 */
export default function BetaAcknowledgmentModal() {
  const { user, refresh } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const requiredVersion = user?.beta_ack_required_version ?? 1;
  const acceptedVersion = user?.beta_ack_version_accepted ?? 0;
  const shouldShow = !!user && acceptedVersion < requiredVersion;

  // Block background scroll + suppress ESC while modal is up so nothing
  // else in the app becomes reachable behind the acknowledgment.
  useEffect(() => {
    if (!shouldShow) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const suppressEsc = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", suppressEsc, true);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", suppressEsc, true);
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const submit = async () => {
    if (!checked || busy) return;
    setBusy(true);
    try {
      await api.post("/users/me/beta-ack", { accepted: true, version: requiredVersion });
      await refresh();
      toast.success("Thanks — you're all set.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="beta-ack-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="beta-ack-modal"
    >
      {/* Solid backdrop — clicks intentionally do nothing */}
      <div className="absolute inset-0 bg-foreground/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-[hsl(var(--sb-cream))] shadow-2xl">
        <div className="p-6 sm:p-7 max-h-[85vh] overflow-y-auto">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))]">
              <ShieldAlert size={18} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-[hsl(var(--sb-clay))]">
              Beta acknowledgment
            </span>
          </div>
          <h2
            id="beta-ack-title"
            className="mt-4 font-serif text-2xl leading-tight"
            data-testid="beta-ack-title"
          >
            A quick note before you continue
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/90">
            <p>
              <span className="font-medium">Last Bookmark is currently in beta</span> and features
              may change as we continue improving the platform.
            </p>
            <p>
              You can now <span className="font-medium">create projects, apply to projects, and
              connect with other users</span>. We're grateful you're here early to help shape the
              community.
            </p>
            <div className="rounded-xl border border-[hsl(var(--sb-clay))]/30 bg-[hsl(var(--sb-clay))]/8 p-3.5 space-y-2">
              <p className="font-medium text-[hsl(var(--sb-clay))]">Payments notice</p>
              <ul className="list-disc pl-5 space-y-1.5 text-foreground/90">
                <li>
                  Last Bookmark <span className="font-medium">does not currently process or
                  protect payments</span>.
                </li>
                <li>
                  Any payment arrangements are made <span className="font-medium">directly between
                  users and are at the users' own risk</span>.
                </li>
                <li>
                  Last Bookmark is <span className="font-medium">not responsible for payment
                  disputes, refunds, or agreements</span> between users during the beta.
                </li>
                <li>
                  <span className="font-medium">Once secure in-platform payments are available,
                  completed paid projects will include a small platform service fee</span> to help
                  support payment processing, website operation, and ongoing improvements. The
                  exact fee will be shown before either party agrees to payment.
                </li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              Read the full{" "}
              <a
                href="/beta-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline text-foreground"
                data-testid="beta-ack-full-terms-link"
              >
                Beta Terms
              </a>
              {" "}·{" "}
              <a
                href="/payment-guidance"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline text-foreground"
                data-testid="beta-ack-payment-guidance-link"
              >
                Safe payments during beta
              </a>
            </p>
          </div>

          <label
            htmlFor="beta-ack-checkbox"
            className={`mt-5 flex items-start gap-3 rounded-2xl border p-3.5 cursor-pointer transition-colors ${
              checked
                ? "border-[hsl(var(--sb-forest))]/60 bg-[hsl(var(--sb-moss))]/50"
                : "bg-muted/30 hover:bg-muted/50"
            }`}
            data-testid="beta-ack-checkbox-wrapper"
          >
            <input
              id="beta-ack-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
              data-testid="beta-ack-checkbox"
            />
            <span className="text-sm leading-relaxed text-foreground">
              I understand that Last Bookmark is in beta, I have read this notice, and I accept
              the beta terms, including the current payment risks and future platform service
              fee disclosure.
            </span>
          </label>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={!checked || busy}
              className="btn-primary min-w-[140px]"
              data-testid="beta-ack-continue"
            >
              <Check size={14} /> {busy ? "Saving…" : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
